import { getEnv } from "../config/env";
import { loadSession } from "./auth-store";
import { getMode } from "./mode";

// The app's ONLY backend interface: a typed fetch client for Supabase Edge
// Functions (ADR-0013). No supabase-js, no direct table access, no schema knowledge.

export class FunctionsError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "FunctionsError";
  }
}

export interface CallOptions {
  method?: "GET" | "POST";
  body?: unknown;
  /** The user's bearer token, when the function requires an authenticated caller. */
  token?: string;
}

export async function callFunction<T>(name: string, opts: CallOptions = {}): Promise<T> {
  const { VITE_FUNCTIONS_URL, VITE_SUPABASE_ANON_KEY } = getEnv();
  // Attach the stored session token by default so authenticated calls just work.
  const token = opts.token ?? loadSession()?.access_token;
  const res = await fetch(`${VITE_FUNCTIONS_URL}/${name}`, {
    method: opts.method ?? "POST",
    headers: {
      "content-type": "application/json",
      apikey: VITE_SUPABASE_ANON_KEY,
      // Test/Live sandbox: every call carries the current mode so the backend returns/writes
      // the isolated test dataset (or the real live data). Default 'live'.
      "x-rt-mode": getMode(),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (!res.ok) {
    // Surface the server's `{ error }` message (e.g. "verify your identity …")
    // instead of a generic failure, so the UI can tell the user what to do.
    let message = `Edge function "${name}" failed`;
    try {
      const body = (await res.json()) as { error?: unknown };
      if (typeof body?.error === "string" && body.error) message = body.error;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new FunctionsError(message, res.status);
  }
  return (await res.json()) as T;
}

export interface HealthResult {
  status: string;
  db: string;
  time?: string;
}

export const getHealth = () => callFunction<HealthResult>("health", { method: "GET" });
